import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../store/slices/authSlice";
import { completeEmailLinkSignIn, checkIsEmailSignInLink } from "../firebase";
import CustomClinikitButton from "../components/ui/CustomClinikitButton";
import AuthLayout from "../components/auth/AuthLayout";
import { WarningCircle } from "@phosphor-icons/react";
import LoaderLoop from "../components/shared/loader/LoaderLoop";
import "./AuthPages.css";

function EmailLinkAuthPage() {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const [email, setEmail] = useState("");
  const [needsEmail, setNeedsEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const handleSignIn = useCallback(async (signInEmail, url) => {
    setIsLoading(true);
    setError("");

    try {
      await completeEmailLinkSignIn(signInEmail, url || window.location.href);
      // AuthProvider will handle the rest (fetching profile, redirecting)
    } catch (err) {
      console.error("[CK-1008] Email link sign-in error:", {
        errorCode: "CK-1008",
        errorMessage: err.message,
        firebaseCode: err.code,
        timestamp: new Date().toISOString(),
      });

      if (
        err.code === "functions/invalid-argument" ||
        err.code === "auth/invalid-action-code"
      ) {
        setError("הקישור אינו תקין או שפג תוקפו. נסו לבקש קישור חדש.");
        setNeedsEmail(true);
      } else if (
        err.code === "functions/deadline-exceeded" ||
        err.code === "auth/expired-action-code"
      ) {
        setError("הקישור פג תוקף. נסו לבקש קישור חדש.");
      } else if (err.code === "functions/not-found") {
        setError("כתובת המייל לא נמצאה במערכת. צרו קשר עם התמיכה.");
      } else {
        setError(err.message || "שגיאה בהתחברות. נסו שוב.");
      }
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
      return;
    }

    const url = window.location.href;

    if (!checkIsEmailSignInLink(url)) {
      setError("הקישור אינו תקין או שפג תוקפו.");
      setIsLoading(false);
      return;
    }

    const storedEmail = window.localStorage.getItem("emailForSignIn");

    if (storedEmail) {
      handleSignIn(storedEmail, url);
    } else {
      setNeedsEmail(true);
      setIsLoading(false);
    }
  }, [isAuthenticated, navigate, handleSignIn]);

  const handleSubmitEmail = (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("יש להזין כתובת מייל תקינה");
      return;
    }
    handleSignIn(email.trim().toLowerCase());
  };

  const footerLink = (
    <p>
      <Link to="/login">חזרה לדף ההתחברות</Link>
    </p>
  );

  return (
    <AuthLayout variant="login" footer={footerLink}>
          <div className="auth-head">
            <h1>התחברות באמצעות מייל</h1>
          </div>

          {isLoading && !needsEmail && (
            <div className="email-callback-loading">
              <LoaderLoop size="md" />
              <p>מאמת את הקישור...</p>
            </div>
          )}

          {needsEmail && (
            <>
              <p className="auth-subhead">הזינו את כתובת המייל שאליה נשלח הקישור</p>
              <form onSubmit={handleSubmitEmail} className="auth-form">
                <div className="form-group">
                  <label>כתובת מייל</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    placeholder="your@email.com"
                    className="email-login-input"
                    disabled={isLoading}
                    dir="ltr"
                    autoComplete="email"
                    autoFocus
                  />
                  {error && <span className="error-message">{error}</span>}
                </div>

                <CustomClinikitButton
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={isLoading || !email}
                >
                  {isLoading ? "מתחבר..." : "התחבר"}
                </CustomClinikitButton>
              </form>
            </>
          )}

          {!isLoading && !needsEmail && error && (
            <div className="auth-form email-sent-state">
              <div className="email-sent-icon email-sent-icon-warning">
                <WarningCircle size={26} weight="fill" />
              </div>
              <p className="email-sent-message">{error}</p>
              <CustomClinikitButton
                type="button"
                variant="primary"
                fullWidth
                onClick={() => navigate("/login")}
              >
                חזרה לדף ההתחברות
              </CustomClinikitButton>
            </div>
          )}
    </AuthLayout>
  );
}

export default EmailLinkAuthPage;
